// How the app establishes who the user is: Microsoft sign-in through Firebase, everywhere.
//
// Locally the SDK is connected to the Firebase Auth emulator by firebase.js (ADR-0006), and the
// people it offers are the local roster; nothing here knows the difference. The wall, the
// button, the popup and the sign-out are one code path, which is the point.

// Closing the popup without choosing is not a failure the wall needs to explain; anything else
// the popup reports is.
const DISMISSED = ['auth/popup-closed-by-user', 'auth/cancelled-popup-request'];

// The callback startSession was given, so a failed popup can be reported the way a session
// error is - as the wall's error phase - rather than as an unhandled rejection from a click.
let notify = null;

export async function currentUserToken() {
  const { getAuthInstance } = await import('../firebase.js');
  const auth = await getAuthInstance();
  const user = auth.currentUser;
  if (!user) throw new Error('not signed in');

  // getIdToken refreshes when the token is close to expiry, so a long-lived tab does not start
  // sending stale tokens.
  return user.getIdToken();
}

export async function startSession(onState) {
  notify = onState;
  const { getAuthInstance } = await import('../firebase.js');
  const auth = await getAuthInstance();

  // onAuthStateChanged rather than reading currentUser: on load the SDK needs a tick to restore
  // a persisted session, and currentUser is null in the meantime. A completed popup lands here
  // too, so the wall never has to know how the session arrived.
  auth.onAuthStateChanged((user) => {
    onState(user ? { phase: 'authenticated' } : { phase: 'signed-out' });
  });
}

export async function signIn() {
  const { signIn: firebaseSignIn } = await import('../firebase.js');
  try {
    await firebaseSignIn();
  } catch (err) {
    if (DISMISSED.includes(err.code)) return;
    notify?.({ phase: 'error', message: `Sign-in failed: ${err.message}` });
  }
}

export async function signOut() {
  const { signOut: firebaseSignOut } = await import('../firebase.js');
  return firebaseSignOut();
}
